import { FormikProps, getIn } from 'formik';
import React, { useContext } from 'react';
import { formatCurrency } from '../../helpers/stringHelpers';
import { ButtonIcon, SecondaryButton } from '../../shared/Button';
import { FormContext } from '../../shared/form/Form';
import { TextInputField } from '../../shared/form/inputs/TextInputField';
import { Tr } from '../../shared/Table';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { useBillOfMaterialsPricingModelInfo } from './BillOfMaterialsContext';
import {
  ActionButtonContainer,
  ActionButtonTd,
  CurrencyTd,
  DescriptionTd,
  InactivePartErrorIndicator,
  PartCodeContainer,
  PartCodeTd,
  QuantityTd,
} from './BillOfMaterialsItemsTable';
import { BillOfMaterialsOptionalExtraFormModel } from './billOfMaterialsOptionalExtraFormModel';
import { ReactComponent as DeleteIcon } from '../../images/icons/delete.svg';
import { BillOfMaterialsPartsById, getPartPriceBasedOnCustomerType } from './dataForEditBillOfMaterialsForm';

type Props = {
  fieldName: string;
  controlExtra: BillOfMaterialsOptionalExtraFormModel;
  partsById: BillOfMaterialsPartsById;
  deleteControlExtra: () => void;
  errors: FormikProps<EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel>['errors'];
};

export const BillOfMaterialsControlExtraField = ({
  fieldName,
  controlExtra,
  partsById,
  deleteControlExtra,
  errors,
}: Props) => {
  const { isDisabled } = useContext(FormContext);
  const { isUsingLegacyPricingModel, selectedCustomerTypeOption } = useBillOfMaterialsPricingModelInfo();

  const part = controlExtra.partId == null ? null : partsById[controlExtra.partId] || null;

  if (part == null) {
    throw new Error(`Couldn't find Part with ID ${controlExtra.partId}`);
  }

  const priceDifference =
    Number(getPartPriceBasedOnCustomerType(isUsingLegacyPricingModel, part, selectedCustomerTypeOption)) *
    Number(controlExtra.quantity);

  const partIsInactiveValidationFieldName = `${fieldName}.partIsInactiveValidation`;
  const partIsInactiveValidationError = getIn(errors, partIsInactiveValidationFieldName);

  return (
    <Tr>
      <PartCodeTd>
        <PartCodeContainer $hasError={partIsInactiveValidationError != null}>
          {part.name}
          <InactivePartErrorIndicator
            fieldName={partIsInactiveValidationFieldName}
            showError={partIsInactiveValidationError != null}
            error={partIsInactiveValidationError}
          />
        </PartCodeContainer>
      </PartCodeTd>
      <QuantityTd>
        <TextInputField
          name={`${fieldName}.quantity`}
          label={null}
          testId={billOfMaterialsControlOptionalExtraQuantityFieldTestId(fieldName)}
        />
      </QuantityTd>
      <DescriptionTd>{part.description}</DescriptionTd>
      <CurrencyTd>{isNaN(priceDifference) ? null : formatCurrency(priceDifference)}</CurrencyTd>
      <ActionButtonTd>
        {!isDisabled && (
          <ActionButtonContainer>
            <SecondaryButton onClick={deleteControlExtra} disabled={false} title="Delete">
              <ButtonIcon>
                <DeleteIcon
                  title="Delete"
                  data-testid={deleteBillOfMaterialsControlOptionalExtraButtonTestId(fieldName)}
                />
              </ButtonIcon>
            </SecondaryButton>
          </ActionButtonContainer>
        )}
      </ActionButtonTd>
    </Tr>
  );
};

export const deleteBillOfMaterialsControlOptionalExtraButtonTestId = (fieldName: string) =>
  `delete-bom-control-optional-extra-${fieldName}`;

export const billOfMaterialsControlOptionalExtraQuantityFieldTestId = (fieldName: string) =>
  `bom-control-optional-extra-quantity-${fieldName}`;
